import { useCallback, useEffect, useRef, useState } from "react";
import { Chess } from "chess.js";
import {
  isPopcorn,
  type GameDto,
  type MoveDto,
  type PlayerDto,
  type Side,
  type UserDto,
} from "@chess/shared";

import { AddBotControl } from "./AddBotControl.js";
import { ApiError, joinGame } from "./api.js";
import { Board, type Promotion } from "./Board.js";
import { Clock } from "./Clock.js";
import { DemocracyLobby } from "./DemocracyLobby.js";
import { MoveList } from "./MoveList.js";
import { navigate } from "./router.js";
import { TeamStatus } from "./TeamStatus.js";
import { useGameSocket } from "./useGameSocket.js";

type CapturablePiece = "p" | "n" | "b" | "r" | "q";

const PIECE_VALUE: Record<CapturablePiece, number> = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
};

const PIECE_GLYPH: Record<Side, Record<CapturablePiece, string>> = {
  white: { p: "♙", n: "♘", b: "♗", r: "♖", q: "♕" },
  black: { p: "♟", n: "♞", b: "♝", r: "♜", q: "♛" },
};

interface Captures {
  white: CapturablePiece[];
  black: CapturablePiece[];
}

function capturesAtPly(moves: readonly MoveDto[], ply: number): Captures {
  const chess = new Chess();
  const captures: Captures = { white: [], black: [] };

  for (const entry of moves.slice(0, ply)) {
    const promotion = entry.uci[4] as Promotion | undefined;
    const move = chess.move({
      from: entry.uci.slice(0, 2),
      to: entry.uci.slice(2, 4),
      ...(promotion ? { promotion } : {}),
    });
    if (!move.captured || move.captured === "k") continue;
    const lostBy: Side = move.color === "w" ? "black" : "white";
    captures[lostBy].push(move.captured as CapturablePiece);
  }

  // Group the most meaningful material first instead of following capture
  // chronology, so the tiny summary is easy to scan at a glance.
  for (const side of ["white", "black"] as const) {
    captures[side].sort((left, right) => PIECE_VALUE[right] - PIECE_VALUE[left]);
  }
  return captures;
}

function materialLost(pieces: readonly CapturablePiece[]): number {
  return pieces.reduce((total, piece) => total + PIECE_VALUE[piece], 0);
}

/** The one line telling a player what, if anything, they should be doing. */
function describeStatus({
  game,
  mySide,
  myTurn,
  hasVoted,
  isDemocracy,
  popcorn,
  playersFor,
}: {
  game: GameDto;
  mySide: Side | null;
  myTurn: boolean;
  hasVoted: boolean;
  isDemocracy: boolean;
  popcorn: boolean;
  playersFor: (side: Side) => PlayerDto[];
}): string {
  if (game.status === "finished") return describeResult(game, mySide);

  const teamName = game.turn === "white" ? "White" : "Black";

  if (popcorn) {
    if (myTurn) return "Your turn — make the move";
    const active = game.players.find(
      (player) => player.userId === game.democracy?.activePlayerId,
    );
    if (!active) return `${teamName} team to move`;
    // Naming the player is the whole point of the mode; "waiting" alone would
    // leave a team unable to tell who they're waiting on.
    return mySide === game.turn
      ? `Waiting on ${active.username}`
      : `${active.username} is playing for ${teamName}`;
  }

  if (isDemocracy) {
    if (myTurn) return "Lock in your team’s move";
    if (hasVoted) return "Your move is locked in";
    return `${teamName} team is voting`;
  }

  if (myTurn) return "Your move";
  const opponents = playersFor(game.turn).map((player) => player.username).join(", ");
  return `${opponents || "Opponent"} to move`;
}

/** A move this player has locked in but that the team hasn't resolved yet. */
interface Ballot {
  ply: number;
  /** Position it was played from — where the board rewinds to. */
  beforeFen: string;
  /** Position it produced — what the board shows while teammates decide. */
  afterFen: string;
  uci: string;
  /** When it was locked, used to guarantee it stays on screen long enough. */
  at: number;
}

/**
 * Minimum time your own move stays on the board before any rewind.
 *
 * Whoever votes last resolves the round instantly, so without this the mover
 * would see their piece for a single frame and then watch it snap elsewhere —
 * exactly the confusion the rewind exists to prevent.
 */
const SHOW_OWN_MOVE_MS = 700;

/** How long the rewound position is held before the winning move plays. */
const REWIND_HOLD_MS = 850;
const VOTE_HINT_DISMISSED_KEY = "team-chess-vote-hint-dismissed";

function voteHintWasDismissed(): boolean {
  try {
    return window.localStorage.getItem(VOTE_HINT_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

function sideOf(game: GameDto, userId: string): Side | null {
  return game.players.find((player) => player.userId === userId)?.side ?? null;
}

function describeResult(game: GameDto, mySide: Side | null): string {
  const reason = game.resultReason?.replace(/_/g, " ") ?? "";
  if (game.result === "draw") return game.resultReason === "draw_agreed" ? "Draw agreed." : `Draw by ${reason}.`;
  if (!game.result) return "";
  const who = game.result === mySide ? "Your team wins" : `${game.result === "white" ? "White" : "Black"} wins`;
  return game.resultReason === "timeout" ? `${who} on time.` : `${who} by ${reason}.`;
}

/** Rebuilds a historical position from the game's persisted UCI move list. */
function positionAtPly(moves: readonly MoveDto[], ply: number): string {
  const chess = new Chess();
  for (const move of moves.slice(0, ply)) {
    const promotion = move.uci[4] as Promotion | undefined;
    chess.move({
      from: move.uci.slice(0, 2),
      to: move.uci.slice(2, 4),
      ...(promotion ? { promotion } : {}),
    });
  }
  return chess.fen();
}

/**
 * The strip beside the board. Roster and voting live in TeamStatus; this keeps
 * the playing context here: side, captured material, and clock.
 */
function Seat({ side, game, gameAt, mySide, captured, materialLead }: {
  side: Side;
  game: GameDto;
  gameAt: number;
  mySide: Side | null;
  captured: readonly CapturablePiece[];
  /** Present only for the side currently ahead on material. */
  materialLead: number | null;
}) {
  const clock = game.clock;
  const capturedSide: Side = side === "white" ? "black" : "white";
  const materialDescription =
    materialLead === null ? "" : `${side} is ahead by ${materialLead} point${materialLead === 1 ? "" : "s"}`;
  return (
    <div className={`seat${game.turn === side && game.status === "active" ? " seat--turn" : ""}`}>
      <span className={`piece-dot piece-dot--${side}`} aria-hidden />
      <span className="seat__name">
        {side === "white" ? "White" : "Black"}
        {mySide === side ? <span className="tag tag--quiet">your team</span> : null}
      </span>
      {captured.length > 0 ? (
        <span className="seat__captured" aria-label={`${side} has captured ${captured.length} piece${captured.length === 1 ? "" : "s"}`}>
          <span className="seat__captured-pieces" aria-hidden>
            {captured.map((piece, index) => <span key={`${piece}-${index}`}>{PIECE_GLYPH[capturedSide][piece]}</span>)}
          </span>
        </span>
      ) : null}
      {materialLead !== null ? (
        <span className="seat__material" aria-label={materialDescription}>
          +{materialLead}
        </span>
      ) : null}
      {clock ? <Clock ms={side === "white" ? clock.whiteMs : clock.blackMs} running={clock.runningFor === side} asOf={gameAt} label={side} /> : null}
    </div>
  );
}

export function GamePage({ gameId, user }: { gameId: string; user: UserDto }) {
  const { game, gameAt, online, error, connected, propose, ready, resign, offerDraw, respondToDraw, clearError } = useGameSocket(gameId);
  // The move this player locked in, held locally so the board can show it while
  // the rest of the team is still deciding. Not on the server: ballots stay
  // secret until the round resolves, so a reload legitimately forgets it.
  const [ballot, setBallot] = useState<Ballot | null>(null);
  // Set while replaying: the board steps back to the pre-move position so the
  // move that actually won is visibly played from the same place yours was.
  const [rewinding, setRewinding] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showResolvedVote, setShowResolvedVote] = useState(false);
  const [voteHintDismissed, setVoteHintDismissed] = useState(voteHintWasDismissed);
  const [reviewPly, setReviewPly] = useState<number | null>(null);
  const seenResolution = useRef(0);

  const mySide = game ? sideOf(game, user.id) : null;
  const isDemocracy = game?.mode === "democracy";
  const hasVoted = Boolean(game?.democracy?.voters.includes(user.id));
  const popcorn = isPopcorn(game?.democracyVoteMode ?? null);
  // In popcorn the turn belongs to one named teammate, so being on the side to
  // move isn't enough — the board stays inert for everyone else.
  const myPopcornTurn = !popcorn || game?.democracy?.activePlayerId === user.id;
  const myTurn = Boolean(
    game &&
      game.status === "active" &&
      mySide === game.turn &&
      (!isDemocracy || !hasVoted) &&
      myPopcornTurn,
  );

  const dismissVoteOverlay = useCallback(() => {
    setShowResolvedVote(false);
    if (voteHintDismissed) return;
    setVoteHintDismissed(true);
    try {
      window.localStorage.setItem(VOTE_HINT_DISMISSED_KEY, "true");
    } catch {
      // The hint still stays out of the way for this visit if storage is unavailable.
    }
  }, [voteHintDismissed]);

  // A rejected proposal never became a ballot, so drop it and snap back.
  useEffect(() => { if (error) { setBallot(null); setRewinding(false); } }, [error]);

  /*
   * Reconcile the locked ballot with what the team actually played.
   *
   * If your move won, the board is already showing the right position and
   * nothing should move. If it didn't, step back to the position you moved
   * from and let the winning move play forward from there — otherwise pieces
   * teleport and it's impossible to tell whose move was chosen.
   */
  const committedPly = game?.moves.length ?? 0;
  /*
   * Depend on the committed move's UCI rather than the moves array itself.
   * `game.moves` is a fresh array on every state broadcast, so depending on it
   * tore down and restarted these timers each time — and broadcasts arriving
   * faster than the rewind lasts meant the ballot never cleared, leaving the
   * board stuck on a stale position.
   */
  const committedUci = ballot ? (game?.moves[ballot.ply - 1]?.uci ?? null) : null;
  useEffect(() => {
    if (!ballot || committedPly < ballot.ply) return;

    if (!committedUci || committedUci === ballot.uci) {
      setBallot(null);
      return;
    }

    // Let your move be seen first, however fast the round resolved.
    const linger = Math.max(0, SHOW_OWN_MOVE_MS - (Date.now() - ballot.at));
    let release: ReturnType<typeof setTimeout>;
    const start = setTimeout(() => {
      setRewinding(true);
      release = setTimeout(() => {
        setRewinding(false);
        setBallot(null);
      }, REWIND_HOLD_MS);
    }, linger);

    return () => {
      clearTimeout(start);
      clearTimeout(release);
    };
  }, [ballot, committedPly, committedUci]);
  useEffect(() => {
    const latest = game?.democracy?.resolutions[0];
    if (!latest) return;
    if (latest.ply > seenResolution.current) {
      seenResolution.current = latest.ply;
      setShowResolvedVote(true);
    }
  }, [game?.democracy?.resolutions]);
  useEffect(() => {
    if (!game?.democracy?.resolutions[0]) return;
    const toggleVoteResult = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
      if (event.code === "Space") {
        event.preventDefault();
        if (showResolvedVote) dismissVoteOverlay();
        else setShowResolvedVote(true);
      } else if (event.key === "Escape" && showResolvedVote) {
        event.preventDefault();
        dismissVoteOverlay();
      }
    };
    window.addEventListener("keydown", toggleVoteResult);
    return () => window.removeEventListener("keydown", toggleVoteResult);
  }, [game?.democracy?.resolutions, showResolvedVote, dismissVoteOverlay]);
  useEffect(() => {
    // Finished games open directly in review, as before. During a live game a
    // review position is deliberately left alone when new moves arrive: the
    // player is looking at history, not a moving target.
    if (game?.status === "finished") setReviewPly(game.moves.length);
    else if (game?.status === "waiting") setReviewPly(null);
  }, [game?.status, game?.moves.length]);

  /*
   * Return to the live board the moment the turn becomes yours.
   *
   * A reviewed position makes the board inert, so without this a player who
   * had stepped back through history — or simply pressed an arrow key — was
   * left unable to move at all, with the status still reading "Your move".
   * Only the transition triggers it: choosing to review during your own turn
   * is left alone, since nothing is blocked by it.
   */
  const wasMyTurn = useRef(false);
  useEffect(() => {
    if (myTurn && !wasMyTurn.current) setReviewPly(null);
    wasMyTurn.current = myTurn;
  }, [myTurn]);
  useEffect(() => {
    if (!game || game.status === "waiting") return;
    const review = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      // Nothing to step back to yet: swallowing the key here would park an
      // empty board in review and make it inert for no reason.
      if (event.key === "ArrowLeft" && game.moves.length === 0) return;
      event.preventDefault();
      if (event.key === "ArrowLeft") {
        setReviewPly((current) => Math.max(0, (current ?? game.moves.length) - 1));
      } else {
        setReviewPly((current) => {
          // At the final reviewed position, one more Right restores the live
          // board so a player can immediately make their real move.
          if (current === null || current >= game.moves.length) return null;
          return current + 1;
        });
      }
    };
    window.addEventListener("keydown", review);
    return () => window.removeEventListener("keydown", review);
  }, [game?.status, game?.moves.length]);

  const handleMove = useCallback((from: string, to: string, promotion?: Promotion) => {
    if (!game || !myTurn) return false;

    // Check locally first so an illegal move is refused instantly instead of
    // waiting on a round trip. The server re-validates regardless.
    const probe = new Chess(game.fen);
    let made;
    try {
      made = probe.move({ from, to, promotion });
      if (!made) return false;
    } catch {
      return false;
    }

    clearError();
    setBallot({
      ply: game.moves.length + 1,
      beforeFen: game.fen,
      afterFen: probe.fen(),
      uci: `${made.from}${made.to}${made.promotion ?? ""}`,
      at: Date.now(),
    });
    propose({ from, to, ...(promotion ? { promotion } : {}) });
    return true;
  }, [game, myTurn, propose, clearError]);

  async function handleJoin() {
    try { await joinGame(gameId); setJoinError(null); }
    catch (cause) { setJoinError(cause instanceof ApiError ? cause.message : "Couldn't join this game."); }
  }
  async function handleCopyLink() { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }

  if (!game) return <div className="card"><p className="muted">{connected ? "Loading game…" : "Connecting…"}</p>{error ? <p className="error">{error}</p> : null}</div>;

  if (game.mode === "democracy" && game.status === "waiting") {
    return <DemocracyLobby game={game} user={user} online={online} ready={ready} />;
  }

  const orientation = mySide ?? "white";
  const topSide: Side = orientation === "white" ? "black" : "white";
  const latestResolution = game.democracy?.resolutions[0] ?? null;
  // Review is local-only. The shared game keeps receiving moves and clocks
  // while this board is parked on a historical position.
  /*
   * Two distinct things, previously conflated into one flag:
   *
   *  - the review panel being open, and
   *  - the board being parked on a position that isn't the live one.
   *
   * Only the second may make the board inert. Treating them as the same meant
   * that stepping forward to the newest move left the board showing exactly the
   * live position while still refusing input — the most confusing possible
   * state, because nothing on screen looked wrong.
   */
  const reviewOpen = reviewPly !== null;
  const currentReviewPly = reviewPly ?? game.moves.length;
  const reviewing = reviewOpen && currentReviewPly < game.moves.length;
  const reviewMove = reviewOpen && currentReviewPly > 0 ? game.moves[currentReviewPly - 1] : null;
  const reviewResolution = reviewOpen ? game.democracy?.resolutions.find((resolution) => resolution.ply === currentReviewPly) ?? null : null;
  const visibleProposals = reviewOpen ? reviewResolution?.proposals ?? [] : showResolvedVote ? latestResolution?.proposals ?? [] : [];
  const lastMove = game.moves[game.moves.length - 1];
  /*
   * While a ballot is outstanding it owns the board, even after the resolution
   * arrives. Falling through to game.fen the moment the move count changes
   * would flash the winning position for one frame before the rewind, which
   * reads as a glitch and defeats the point. The reconcile effect clears the
   * ballot when it's finished, and only then does shared truth take over.
   */
  const boardFen = reviewing
    ? positionAtPly(game.moves, currentReviewPly)
    : // An interactive board must always show the live position. Whatever you
      // locked has long since resolved by the time the turn returns to you, so
      // a lingering ballot must never be what you are asked to move from.
      ballot && !myTurn
      ? rewinding
        ? ballot.beforeFen
        : ballot.afterFen
      : game.fen;
  const captured = capturesAtPly(game.moves, reviewOpen ? currentReviewPly : game.moves.length);
  const whiteMaterialLead = materialLost(captured.black) - materialLost(captured.white);
  const materialLeadFor = (side: Side): number | null => {
    const balance = side === "white" ? whiteMaterialLead : -whiteMaterialLead;
    return balance > 0 ? balance : null;
  };
  const playersFor = (side: Side) => game.players.filter((player) => player.side === side);
  // Reviewing wins the status line. The board is inert while parked on history,
  // so a status still reading "Your move" is the difference between a player
  // understanding the board and thinking the app is broken.
  const status = reviewing
    ? `Reviewing move ${currentReviewPly} of ${game.moves.length}`
    : describeStatus({
        game,
        mySide,
        myTurn,
        hasVoted,
        isDemocracy,
        popcorn,
        playersFor,
      });

  return (
    <div className="game">
      <div className="game__board">
        <Seat
          side={topSide}
          game={game}
          gameAt={gameAt}
          mySide={mySide}
          captured={captured[topSide === "white" ? "black" : "white"]}
          materialLead={materialLeadFor(topSide)}
        />
        <Board
          position={boardFen}
          orientation={orientation}
          interactive={myTurn && !reviewing}
          lastMoveUci={reviewing ? reviewMove?.uci : rewinding ? undefined : lastMove?.uci}
          voteProposals={visibleProposals}
          voteResolved={!reviewing && showResolvedVote}
          showVoteHint={!voteHintDismissed}
          onDismissVoteOverlay={dismissVoteOverlay}
          onMove={handleMove}
        />
        {/*
          Reviewing during your own turn is allowed, but the board is inert
          while parked on history. Without an obvious way back this reads as a
          frozen game, which is exactly the confusion being fixed.
        */}
        {reviewing && myTurn ? (
          <button type="button" className="review-return" onClick={() => setReviewPly(null)}>
            Your move — return to live
          </button>
        ) : null}
        <Seat
          side={orientation}
          game={game}
          gameAt={gameAt}
          mySide={mySide}
          captured={captured[orientation === "white" ? "black" : "white"]}
          materialLead={materialLeadFor(orientation)}
        />
      </div>
      <aside className="game__side">
        <section className="card">
          <div className="status"><span className={`dot ${connected ? "dot--on" : "dot--off"}`} aria-hidden /><strong>{status}</strong></div>
          {game.status === "waiting" && !mySide ? <button type="button" onClick={() => void handleJoin()}>Join this game</button> : null}
          {game.status === "waiting" && mySide ? <button type="button" className="button--secondary" onClick={() => void handleCopyLink()}>{copied ? "Link copied" : "Copy invite link"}</button> : null}
          {game.status === "waiting" && mySide && !isDemocracy ? (
            <AddBotControl gameId={game.id} side={mySide === "white" ? "black" : "white"} label="Play a bot" />
          ) : null}
          {reviewOpen ? (
            <section className="game-review" aria-label="Game review">
              <div className="game-review__head">
                <strong>{currentReviewPly === 0 ? "Starting position" : `Move ${currentReviewPly}`}</strong>
                <span><kbd>←</kbd> <kbd>→</kbd></span>
              </div>
              <div className="game-review__controls">
                <button type="button" className="button--secondary" disabled={currentReviewPly === 0} onClick={() => setReviewPly((ply) => Math.max(0, (ply ?? game.moves.length) - 1))}>Previous</button>
                <span>{currentReviewPly}/{game.moves.length}</span>
                <button
                  type="button"
                  className="button--secondary"
                  onClick={() => setReviewPly((ply) => {
                    const at = ply ?? game.moves.length;
                    return at >= game.moves.length ? null : at + 1;
                  })}
                >
                  {currentReviewPly === game.moves.length ? "Live" : "Next"}
                </button>
              </div>
              {reviewMove ? <p><strong>{reviewMove.san}</strong> selected from {game.players.find((player) => player.userId === reviewMove.userId)?.username ?? "a player"}{reviewResolution ? ` · ${reviewResolution.proposals.length} ballots` : ""}</p> : <p>Before the first move.</p>}
            </section>
          ) : null}
          {game.status === "active" && mySide ? (game.drawOfferedBy && game.drawOfferedBy !== mySide ? <div className="offer"><p>The other team offers a draw.</p><div className="row"><button type="button" onClick={() => respondToDraw(true)}>Accept</button><button type="button" className="button--secondary" onClick={() => respondToDraw(false)}>Decline</button></div></div> : <div className="row"><button type="button" className="button--secondary" onClick={resign}>Resign</button><button type="button" className="button--secondary" disabled={game.drawOfferedBy === mySide} onClick={offerDraw}>{game.drawOfferedBy === mySide ? "Draw offered" : "Offer draw"}</button></div>) : null}
          {!mySide && game.status !== "waiting" ? <p className="muted">You’re watching this game.</p> : null}
          {joinError ? <p className="error" role="alert">{joinError}</p> : null}
          {error ? <p className="error" role="alert">{error}</p> : null}
        </section>
        <TeamStatus game={game} gameAt={gameAt} userId={user.id} online={online} />
        <MoveList moves={game.moves} players={game.players} selectedPly={reviewOpen ? currentReviewPly : undefined} />
        <button type="button" className="button--secondary" onClick={() => navigate("/")}>Back to games</button>
      </aside>
    </div>
  );
}
